/* Domains & SSL — handover §14 step 8.

   Two clocks per domain and they fail differently. A lapsed certificate breaks
   the site until somebody renews it; a lapsed registration can lose the domain
   to somebody else, and with it the mail. So the table shows both and sorts on
   whichever is closer, rather than picking one and burying the other.

   The mail records are the third thing. SPF, DKIM and DMARC are long strings
   nobody reads in a table, but whether each one *exists* is a one-glance answer
   and a missing DMARC is how a supplier gets an invoice from "you". They read
   as three badges here and in full in the drawer. */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { daysUntil, expiryTone, fmtDate } from '../../lib/format';
import type { IDomain } from '../../models';
import { dmarcPolicy, soonest } from '../../lib/registers';
import { domainFields, valuesSeen } from '../../lib/registerFields';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  Segmented,
  StatCard,
  StatGrid,
  TableSkeleton,
  type Column,
} from '../ui';
import { EditDrawer } from './EditDrawer';
import s from './screens.module.scss';

export function DomainsScreen() {
  const svc = useServices();

  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<IDomain | null>(null);
  const [adding, setAdding] = useState(false);

  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });
  const qc = useQueryClient();

  const close = () => {
    setSelected(null);
    setAdding(false);
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.domains });
    void qc.invalidateQueries({ queryKey: qk.audit });
    close();
  };

  const save = useMutation({

    meta: { ok: 'Domain saved' },
    mutationFn: (values: Record<string, unknown>) => {
      const patch = values as unknown as Omit<IDomain, 'id'>;
      return selected ? svc.domains.update(selected.id, patch) : svc.domains.create(patch);
    },
    onSuccess: refresh,
  });

  const remove = useMutation({

    meta: { ok: 'Domain deleted' },
    mutationFn: (id: string) => svc.domains.remove(id),
    onSuccess: refresh,
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (domains.data ?? []).filter((d) => {
      if (lens === 'expiring' && !(soonest(d) && daysUntil(soonest(d)) <= 90)) return false;
      if (lens === 'mail' && dmarcPolicy(d.dmarc) === 'reject' && d.spf && d.dkim) return false;
      if (lens === 'ssl' && !(d.sslEnds && daysUntil(d.sslEnds) <= 30)) return false;
      if (!q) return true;
      return d.name.toLowerCase().includes(q) || d.reg.toLowerCase().includes(q);
    });
  }, [domains.data, lens, query]);

  const totals = useMemo(() => {
    const all = domains.data ?? [];
    return {
      expiring: all.filter((d) => soonest(d) && daysUntil(soonest(d)) <= 90).length,
      /* Anything short of p=reject leaves room for somebody to send as this
         domain. p=none in particular reads as "DMARC configured" and enforces
         nothing at all. */
      unenforced: all.filter((d) => dmarcPolicy(d.dmarc) !== 'reject').length,
      noSpf: all.filter((d) => !d.spf?.trim()).length,
    };
  }, [domains.data]);

  const mailBadges = (d: IDomain) => {
    const policy = dmarcPolicy(d.dmarc);
    return (
      <div className={s.tags}>
        <Badge tone={d.spf?.trim() ? 'ok' : 'bad'}>SPF</Badge>
        <Badge tone={d.dkim?.trim() ? 'ok' : 'bad'}>DKIM</Badge>
        <Badge
          tone={policy === 'reject' ? 'ok' : policy === 'quarantine' ? 'warn' : 'bad'}
          title={policy === 'none' ? 'p=none monitors and enforces nothing' : undefined}
        >
          {policy === 'missing' ? 'No DMARC' : `DMARC p=${policy}`}
        </Badge>
      </div>
    );
  };

  const columns: Column<IDomain>[] = [
    {
      key: 'name',
      header: 'Domain',
      sortBy: (d) => d.name,
      render: (d) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className="ti ti-world" aria-hidden />
          </span>
          <div>
            <div className={`${s.cellMain} ${s.mono}`}>{d.name}</div>
            <div className={s.cellSub}>{d.reg || 'No registrar recorded'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'regEnds',
      header: 'Registration',
      sortBy: (d) => d.regEnds,
      /* Losing a registration loses the mail with it, which is why this is not
         folded into a single "expires" column with the certificate. */
      render: (d) =>
        d.regEnds ? (
          <Badge tone={expiryTone(d.regEnds)}>{fmtDate(d.regEnds)}</Badge>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'sslEnds',
      header: 'Certificate',
      sortBy: (d) => d.sslEnds,
      render: (d) =>
        d.sslEnds ? (
          <>
            <Badge tone={expiryTone(d.sslEnds)}>{fmtDate(d.sslEnds)}</Badge>
            {d.ssl && <div className={s.cellSub}>{d.ssl}</div>}
          </>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    { key: 'dns', priority: 2, header: 'DNS', sortBy: (d) => d.dns, render: (d) => d.dns || '—' },
    {
      key: 'mail',
      header: 'Mail records',
      sortBy: (d) => (dmarcPolicy(d.dmarc) === 'reject' ? 0 : 1),
      render: mailBadges,
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Domains &amp; SSL</h1>
          <p>
            Registrations, certificates and mail records.
          </p>
        </div>
        <Button primary icon="ti-plus" onClick={() => { setSelected(null); setAdding(true); }}>
          New domain
        </Button>
      </div>

      {domains.isError && <LoadError error={domains.error} what="domains" />}

      <StatGrid>
        <StatCard
          icon="ti-world"
          label="Domains"
          value={domains.isLoading ? '—' : (domains.data?.length ?? 0)}
          tone="info"
          foot="Registered to us"
        />
        <StatCard
          icon="ti-calendar-exclamation"
          label="Expiring in 90 days"
          value={domains.isLoading ? '—' : totals.expiring}
          tone={totals.expiring > 0 ? 'warn' : 'ok'}
          foot={totals.expiring > 0 ? 'Registration or certificate' : 'Nothing close'}
        />
        <StatCard
          icon="ti-mail-off"
          label="DMARC not enforcing"
          value={domains.isLoading ? '—' : totals.unenforced}
          tone={totals.unenforced > 0 ? 'bad' : 'ok'}
          foot={totals.unenforced > 0 ? 'p=none monitors only' : 'All on p=reject'}
        />
        <StatCard
          icon="ti-mail-x"
          label="No SPF record"
          value={domains.isLoading ? '—' : totals.noSpf}
          tone={totals.noSpf > 0 ? 'bad' : 'ok'}
          foot={totals.noSpf > 0 ? 'Anyone can send as these' : 'All covered'}
        />
      </StatGrid>

      <Controls count={`${rows.length} of ${domains.data?.length ?? 0}`}>
        <SearchBox value={query} onChange={setQuery} placeholder="Domain or registrar…" />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'expiring', label: 'Expiring in 90 days' },
            { value: 'ssl', label: 'Certificate inside 30 days' },
            { value: 'mail', label: 'Mail records incomplete' },
          ]}
        />
      </Controls>

      <Panel flush>
        {domains.isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(d) => d.id}
            initialSort={{ key: 'regEnds', dir: 'asc' }}
            onRowClick={setSelected}
            empty={
              <EmptyState icon="ti-world" title="No domains match">
                Try clearing the filters.
              </EmptyState>
            }
          />
        )}
      </Panel>

      <EditDrawer
        open={selected !== null || adding}
        record={selected}
        fields={domainFields(valuesSeen(domains.data, ['dns']))}
        lookups={{ people: [], suppliers: [] }}
        noun="domain"
        icon="ti-world"
        meta={selected && mailBadges(selected)}
        onClose={close}
        onSave={(values) => save.mutate(values)}
        onDelete={selected ? () => remove.mutate(selected.id) : undefined}
        saving={save.isPending}
        deleting={remove.isPending}
        error={save.error}
      >
        {selected && dmarcPolicy(selected.dmarc) === 'none' && (
          <div className={s.ownedNote}>
            <i className="ti ti-mail-off" aria-hidden />
            <span>
              This domain publishes DMARC with <span className={s.mono}>p=none</span>, which
              reports and enforces nothing. Anyone can still send mail as{' '}
              <span className={s.mono}>{selected.name}</span> and it will be delivered.
            </span>
          </div>
        )}
      </EditDrawer>
    </>
  );
}

